import BackIcon from '@mui/icons-material/ArrowBack';
import FileSaver from 'file-saver';
import KeepIcon from '@mui/icons-material/Done';
import PropTypes from 'prop-types';
import React, {useMemo, useState} from 'react';
import {
  Box,
  Button,
  InputAdornment,
  Grid as MuiGrid,
  TextField,
  Typography,
  styled,
} from '@mui/material';
import {useTheme} from '@mui/material/styles';

const Grid = styled(MuiGrid)(({theme}) => ({
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(1),
  ...theme.typography.body1,
}));

const ConfirmBadge = () => {
  const theme = useTheme();

  const parentSize = 77;
  const iconSize = 33;

  return (
    <Box
      borderRadius="50%"
      border={`solid 2px ${theme.palette.success.main}`}
      width={parentSize}
      height={parentSize}
      display="inline-flex"
      alignItems="center"
      justifyContent="center"
    >
      <KeepIcon
        sx={{
          width: iconSize,
          height: iconSize,
          color: theme.palette.success.main,
        }}
      />
    </Box>
  );
};

export function sanitizeLinks(links) {
  return links.map(link => {
    const {href, ...sanitized} = link;
    if (!href) {
      return sanitized;
    }
    // can't use the URL constructor with URL templates (e.g. for tiles)
    const parts = href.split('?');
    if (parts.length !== 2) {
      sanitized.href = href;
      return sanitized;
    }
    const params = new URLSearchParams(parts[1]);
    if (!params.has('api_key')) {
      sanitized.href = href;
      return sanitized;
    }
    params.delete('api_key');
    if (params.size === 0) {
      sanitized.href = parts[0];
      return sanitized;
    }
    sanitized.href = parts[0] + '?' + params.toString();
    return sanitized;
  });
}

const sanitize = feature => {
  const properties = {...feature.properties};
  delete properties._include;

  const sanitized = {
    ...feature,
    properties,
  };

  if (feature.links) {
    sanitized.links = sanitizeLinks(feature.links);
  }

  return sanitized;
};

const saveFeatures = (filename, features) => {
  const nextFeatures = [];
  for (let i = 0, ii = features.length; i < ii; i++) {
    if (features[i].properties._include === true) {
      nextFeatures.push(sanitize(features[i]));
    }
  }

  const geojsonString = JSON.stringify({
    type: 'FeatureCollection',
    features: nextFeatures,
  });
  const geojsonBlob = new Blob([geojsonString], {
    type: 'application/geo+json',
  });
  FileSaver.saveAs(geojsonBlob, filename);
};

export const ExportStep = ({features, onBack, onClose}) => {
  const [filename, setFilename] = useState('');

  // TODO format the counts.
  const [includedCount, excludedCount] = useMemo(() => {
    const included = features.filter(
      f => f.properties._include === true,
    ).length;
    return [included, features.length - included];
  }, [features]);

  return (
    <Box flex={1} flexDirection="column" display="flex">
      <Box display="flex" padding={1}>
        <Box flex={1}>
          <Button startIcon={<BackIcon />} onClick={() => onBack()}>
            Back to review
          </Button>
        </Box>
        <Box>
          <Button onClick={() => onClose()}>Exit</Button>
        </Box>
      </Box>
      <Box flex={1} display="flex" alignItems="center" justifyContent="center">
        <Box width={312}>
          <Box
            display="flex"
            padding={2}
            alignItems="center"
            justifyContent="center"
          >
            <ConfirmBadge />
          </Box>
          <Typography variant="h5">Detects ready for export</Typography>
          <Box paddingTop={4}>
            <Typography variant="overline">Summary</Typography>
          </Box>

          <Grid container>
            <Grid item xs={6}>
              Detects included
            </Grid>

            <Grid item xs={6} align="right">
              {includedCount}
            </Grid>

            <Grid item xs={6}>
              Detects excluded
            </Grid>

            <Grid item xs={6} align="right">
              {excludedCount}
            </Grid>
          </Grid>

          <Box paddingTop={4}>
            <Typography variant="overline">
              Name your file for export
            </Typography>
          </Box>
          <Grid container>
            <Grid item xs={12}>
              <TextField
                variant="outlined"
                placeholder="Filename"
                onChange={evt => setFilename(evt.target.value)}
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">.geojson</InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} align="right">
              <Button
                disabled={filename.length < 1}
                variant="contained"
                color="primary"
                sx={{marginLeft: 1}}
                onClick={() => {
                  // prompt the user to save the features
                  saveFeatures(filename + '.geojson', features);
                  // close the modal.
                  onClose();
                }}
              >
                Export
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Box>
  );
};

ExportStep.propTypes = {
  features: PropTypes.array,
  onBack: PropTypes.func,
  onClose: PropTypes.func,
};
